import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { TurfTalk, setTokens, clearTokens, getAccess, AuthExpiredError } from "@/api/client";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    if (!getAccess()) { setUser(null); setLoading(false); return null; }
    try {
      const me = await TurfTalk.me();
      setUser(me);
      return me;
    } catch (e) {
      // The API client invalidates only the rejected session; do not erase a
      // newer sign-in completed in another tab after that rejection.
      if (e instanceof AuthExpiredError && !getAccess()) setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = useCallback(async (email, password) => {
    const res = await TurfTalk.login(email, password);
    setTokens(res.access_token, res.refresh_token);
    setUser(res.user);
    const me = await TurfTalk.me().catch(() => res.user);
    setUser(me);
    return me;
  }, []);

  const register = useCallback(async (body) => {
    const res = await TurfTalk.register(body);
    if (res.access_token) {
      setTokens(res.access_token, res.refresh_token);
      const me = await TurfTalk.me().catch(() => res.user);
      setUser(me);
      return me;
    }
    return res;
  }, []);

  const logout = useCallback(async () => {
    const refresh = localStorage.getItem("tt.refresh");
    try { if (refresh) await TurfTalk.logout(refresh); } catch { /* noop */ }
    clearTokens();
    setUser(null);
  }, []);

  const value = { user, setUser, loading, login, register, logout, refreshMe };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
